// src/components/pages/simulation/charts/MonteCarloCharts.js
import React, { useMemo, useState, useEffect, useRef } from 'react'; // useRef, useEffect 추가
import Plot from 'react-plotly.js';
import { getCurrencySymbol } from './../utils/MonteCarloUtils'; 
import styles from "../style/MonteCarlo.module.css"; 

const commonLayout = {
    font: { family: "'Pretendard', sans-serif", color: "#343a40" },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    xaxis: { gridcolor: '#e9ecef', linecolor: '#adb5bd' },
    yaxis: { gridcolor: '#e9ecef', linecolor: '#adb5bd', rangemode: "tozero" },
    margin: { l: 60, r: 30, t: 80, b: 60 },
    legend: { orientation: "h", y: 1.1 },
    dragmode: 'pan',
    hovermode: 'x unified',
};

// ... GrowthChart 컴포넌트는 기존과 동일 (생략 가능하나 전체 코드 유지를 위해 포함)
export const GrowthChart = ({ results, simYears, currency, targetAmount, t, currentYear, isPlaying }) => {
    // [1] 툴팁 상태 및 Ref 추가
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const tooltipRef = useRef(null);

    // [2] 외부 클릭 감지 로직 (HistogramChart와 동일)
    useEffect(() => {
        function handleClickOutside(event) {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                setIsTooltipOpen(false);
            }
        }
        if (isTooltipOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("touchstart", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [isTooltipOpen]);

    const data = useMemo(() => {
        const traces = [];
        const animatedX = Array.from({ length: currentYear + 1 }, (_, i) => i);

        if (results.backgroundPaths && isPlaying) {
            const samplePaths = results.backgroundPaths.slice(0, 100); 
            samplePaths.forEach((path) => {
                traces.push({
                    x: animatedX, y: path.slice(0, currentYear + 1), mode: 'lines', type: 'scattergl', 
                    line: { color: 'rgba(19, 22, 206, 0.05)', width: 2 }, hoverinfo: 'skip', showlegend: false
                });
            });
        }
        if (!isPlaying) {
            const p10 = results.percentilePaths.find(p => p.percentile === 10);
            const p90 = results.percentilePaths.find(p => p.percentile === 90);
            if (p10 && p90) {
                traces.push({
                    x: [...animatedX, ...[...animatedX].reverse()],
                    y: [...p90.path.slice(0, currentYear + 1), ...[...p10.path.slice(0, currentYear + 1)].reverse()],
                    fill: 'toself', fillcolor: 'rgba(99, 102, 241, 0.05)', line: { color: 'transparent' }, name: t('montecarlo.percentileLabels.range80')
                });
            }
        }
        const p50 = results.percentilePaths.find(p => p.percentile === 50);
        if (p50) traces.push({ x: animatedX, y: p50.path.slice(0, currentYear + 1), mode: 'lines', name: t('montecarlo.percentileLabels.p50'), line: { color: '#4F46E5', width: 3 } });
        if (results.principalPath) traces.push({ x: animatedX, y: results.principalPath.slice(0, currentYear + 1), type: 'scatter', mode: 'lines', name: t('montecarlo.myPrincipal', "My Principal"), line: { color: '#6c757d', width: 2, dash: 'dash' } });
        return traces;
    }, [results, currentYear, isPlaying, simYears]);

    const layout = useMemo(() => ({
        ...commonLayout,
        title: { 
            text: t('montecarlo.scenarioCount', '{{count}} Scenarios', { count: results.finalValues?.length.toLocaleString() }),
            font: { size: 14, color: '#666' },
            x: 0,
            xanchor: 'left'
        },
        xaxis: { ...commonLayout.xaxis, range: [0, simYears], autorange: false },
        yaxis: { ...commonLayout.yaxis, tickprefix: getCurrencySymbol(currency), title: { text: t('montecarlo.balanceLabel', 'Balance') }, autorange: true },
        shapes: targetAmount > 0 ? [{ type: 'line', x0: 0, x1: simYears, y0: targetAmount, y1: targetAmount, line: { color: '#28a745', width: 2, dash: 'dashdot' } }] : []
    }), [simYears, currency, targetAmount, results.finalValues, currentYear, isPlaying]);

    // [3] JSX 구조 변경: chartWrapper와 chartHeader 추가
    return (
        <div className={styles.chartWrapper}>
            <div className={styles.chartHeader}>
                {/* 제목은 Plot의 title 대신 HTML로 분리하는 것이 툴팁 배치에 유리할 수 있습니다. 
                    여기서는 일관성을 위해 아이콘만 우측 상단에 배치합니다. */}
                <div style={{ flex: 1 }}></div> 
                
                <div 
                    ref={tooltipRef}
                    className={`${styles.infoIconWrapper} ${isTooltipOpen ? styles.open : ''}`}
                    onClick={() => setIsTooltipOpen(!isTooltipOpen)} 
                >
                    <span className={styles.infoIcon}>ⓘ</span>
                    <div className={styles.tooltipContent}>
                       {t('montecarlo.chart.tooltipGrowth')}
                    </div>
                </div>
            </div>
            
            <Plot 
                data={data} 
                layout={layout} 
                useResizeHandler 
                style={{ width: '100%', height: 'calc(100% - 40px)' }} 
                config={{displayModeBar: false}} 
            />
        </div>
    );
};

// -------------------------------------------------------------------------
// HistogramChart: 클릭 외부 감지 로직 적용
// -------------------------------------------------------------------------
export const HistogramChart = ({ dataValues, title, color, xTitle, t, currency, fixedMaxX, isPlaying, isLowerBetter = false }) => {
    const [isFiltered, setIsFiltered] = useState(true);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    
    // [1] 툴팁 영역을 참조하기 위한 Ref 생성
    const tooltipRef = useRef(null);

    // [2] 외부 클릭 감지 로직
    useEffect(() => {
        function handleClickOutside(event) {
            // 클릭된 곳이 tooltipRef 영역 안이 아니면 닫기
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                setIsTooltipOpen(false);
            }
        }

        if (isTooltipOpen) {
            // 툴팁이 열려있을 때만 이벤트 리스너 등록
            document.addEventListener("mousedown", handleClickOutside);
            // 모바일 터치 대응
            document.addEventListener("touchstart", handleClickOutside);
        }

        return () => {
            // 컴포넌트 언마운트 시 또는 툴팁이 닫힐 때 리스너 제거
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [isTooltipOpen]);

    const stats = useMemo(() => {
        if (!dataValues || dataValues.length === 0) return null;
        const sorted = [...dataValues].sort((a, b) => a - b);
        return {
            p10: sorted[Math.floor(sorted.length * 0.1)],
            p50: sorted[Math.floor(sorted.length * 0.5)],
            p90: sorted[Math.floor(sorted.length * 0.9)],
        };
    }, [dataValues]);

    const filteredData = useMemo(() => {
        if (!isFiltered || !stats) return dataValues;
        return dataValues.filter(v => v >= stats.p10 && v <= stats.p90);
    }, [dataValues, isFiltered, stats]);

    const isPercentage = xTitle?.toLowerCase().includes('%') || xTitle?.toLowerCase().includes('drawdown');
    const symbol = getCurrencySymbol(currency);
    const labels = isLowerBetter ? { low: { text: t('montecarlo.percentileLabels.p10upper'), color: '#10b981' }, high: { text: t('montecarlo.percentileLabels.p10lower'), color: '#ef4444' } } 
                                 : { low: { text: t('montecarlo.percentileLabels.p10low'), color: '#ef4444' }, high: { text: t('montecarlo.percentileLabels.p90high'), color: '#10b981' } };

    const chartData = useMemo(() => {
        const traces = [{
            x: filteredData, type: 'histogram', name: t('montecarlo.scenarioFrequency', 'Scenario Frequency'), showlegend: false,
            marker: { color: color, opacity: 0.75, line: { color: color, width: 1 } },
            hovertemplate: `<b>${t('montecarlo.rangeLabel', 'Range')}:</b> %{x}<br><b>${t('montecarlo.frequencyLabel', 'Frequency')}:</b> %{y}<extra></extra>`,
        }];
        if (stats) {
            [{ x: stats.p10, name: labels.low.text, color: labels.low.color, dash: 'dot' },
             { x: stats.p50, name: t('montecarlo.percentileLabels.p50'), color: '#4F46E5', dash: 'solid' },
             { x: stats.p90, name: labels.high.text, color: labels.high.color, dash: 'dot' }].forEach(item => {
                traces.push({ x: [null], y: [null], mode: 'lines', name: item.name, line: { color: item.color, width: 2, dash: item.dash }, showlegend: true });
            });
        }
        return traces;
    }, [filteredData, stats, labels, color]);

    const layout = {
        ...commonLayout,
        showlegend: true, 
        legend: { orientation: "h", y: -0.2, x: 0.5, xanchor: "center", font: { size: 11 } },
        xaxis: { ...commonLayout.xaxis, tickformat: isPercentage ? '.1f' : ',d', tickprefix: isPercentage ? '' : symbol, autorange: isFiltered ? false : true, range: isFiltered && stats ? [stats.p10, stats.p90] : undefined },
        yaxis: { ...commonLayout.yaxis, title: { text: t('montecarlo.frequencyLabel', 'Frequency') } },
        shapes: stats ? [
            { type: 'line', x0: stats.p10, x1: stats.p10, y0: 0, y1: 1, yref: 'paper', line: { color: labels.low.color, width: 2, dash: 'dot' } },
            { type: 'line', x0: stats.p50, x1: stats.p50, y0: 0, y1: 1, yref: 'paper', line: { color: '#4F46E5', width: 2 } },
            { type: 'line', x0: stats.p90, x1: stats.p90, y0: 0, y1: 1, yref: 'paper', line: { color: labels.high.color, width: 2, dash: 'dot' } },
        ] : [],
    };

    const getTooltipContent = () => {
        if (isLowerBetter || xTitle?.toLowerCase().includes('drawdown')) {
            return t('montecarlo.chart.tooltipDrawdown');
        }
        return t('montecarlo.chart.tooltipFinalBalance');
    };

    return (
        <div className={styles.chartWrapper}>
            <div className={styles.chartHeader}>
                <div className={styles.chartControlContainer}>
                    <button 
                        onClick={() => setIsFiltered(!isFiltered)}
                        className={`${styles.chartFilterBtn} ${isFiltered ? styles.active : ''}`}
                        style={{ borderColor: isFiltered ? 'transparent' : color, color: isFiltered ? 'inherit' : color }}
                    >
                        {isFiltered ? t('montecarlo.histogramFilter.showAll') : t('montecarlo.histogramFilter.realisticRange')}
                    </button>
                </div>
                
                {/* [3] Ref 연결: 아이콘과 툴팁을 감싸는 wrapper에 ref를 설정합니다. */}
                <div 
                    ref={tooltipRef}
                    className={`${styles.infoIconWrapper} ${isTooltipOpen ? styles.open : ''}`}
                    onClick={() => setIsTooltipOpen(!isTooltipOpen)} 
                >
                    <span className={styles.infoIcon}>ⓘ</span>
                    <div className={styles.tooltipContent}>
                        {getTooltipContent()}
                    </div>
                </div>
            </div>
            
            <Plot 
                data={chartData} 
                layout={layout} 
                useResizeHandler 
                style={{ width: '100%', height: 'calc(100% - 30px)' }} 
                config={{displayModeBar: false}} 
            />
        </div>
    );
};